import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Ban,
  ChevronLeft,
  ChevronRight,
  Flag,
  Heart,
  Loader2,
  Search,
  Shield,
  Trash2,
  Undo2,
  User,
  X,
} from "lucide-react";
import { toast } from "sonner";
import type { AdminComment, AdminCommentUser } from "../../../../shared/index";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const PAGE_SIZE = 50;
const COMMENTS_QUERY = ["comments", "list"] as const;

function describeError(e: unknown): string {
  return e instanceof ApiError ? e.message : "Something went wrong";
}

function formatDate(dateStr: string): string {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function ModerationBadge({ status }: { status: AdminComment["moderationStatus"] }) {
  const styles: Record<AdminComment["moderationStatus"], string> = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span className={`rounded-md border px-2 py-0.5 text-[11px] font-medium ${styles[status]}`}>
      {status}
    </span>
  );
}

export default function CommentsPage() {
  const [page, setPage] = useState(1);
  const [slugSearch, setSlugSearch] = useState("");
  const [appliedSlug, setAppliedSlug] = useState("");
  const [filterDeleted, setFilterDeleted] = useState(false);
  const [filterReported, setFilterReported] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{ _id: string; displayName: string } | null>(
    null
  );
  const [userQuery, setUserQuery] = useState("");
  const [userResults, setUserResults] = useState<AdminCommentUser[]>([]);
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  const userSearchRef = useRef<HTMLDivElement>(null);

  const queryParams = {
    page,
    limit: PAGE_SIZE,
    postSlug: appliedSlug || undefined,
    userId: selectedUser?._id || undefined,
    deleted: filterDeleted,
    reported: filterReported,
  };

  const commentsQuery = useQuery({
    queryKey: [...COMMENTS_QUERY, queryParams],
    queryFn: () => api.comments.list(queryParams),
  });

  useEffect(() => {
    if (userQuery.length < 1) {
      setUserResults([]);
      return;
    }
    const timeout = setTimeout(() => {
      api.comments
        .searchUsers(userQuery)
        .then((res) => {
          setUserResults(res);
          setUserSearchOpen(true);
        })
        .catch(() => setUserResults([]));
    }, 300);
    return () => clearTimeout(timeout);
  }, [userQuery]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userSearchRef.current && !userSearchRef.current.contains(e.target as Node)) {
        setUserSearchOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setAppliedSlug(slugSearch.trim());
    setPage(1);
  }

  function clearSearch() {
    setSlugSearch("");
    setAppliedSlug("");
    setPage(1);
  }

  function clearUserFilter() {
    setSelectedUser(null);
    setUserQuery("");
    setUserResults([]);
    setPage(1);
  }

  const data = commentsQuery.data;
  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  return (
    <div className="space-y-6">
      <Link
        to="/dashboard/news-portal"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to News Portal
      </Link>

      <header>
        <h1 className="text-2xl font-bold">Comments</h1>
        <p className="text-sm text-muted-foreground">
          {data ? `${data.total} comment${data.total !== 1 ? "s" : ""} found` : "Moderate user comments"}
        </p>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4">
        <form onSubmit={handleSearch} className="flex items-end gap-2">
          <div className="grid gap-1.5">
            <label className="text-xs font-medium text-muted-foreground">Post Slug</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={slugSearch}
                onChange={(e) => setSlugSearch(e.target.value)}
                placeholder="Search by post slug..."
                className="w-72 pl-9"
              />
            </div>
          </div>
          <Button type="submit" size="sm">
            Search
          </Button>
          {appliedSlug && (
            <Button type="button" variant="ghost" size="sm" onClick={clearSearch}>
              Clear
            </Button>
          )}
        </form>

        {/* User search */}
        <div className="grid gap-1.5" ref={userSearchRef}>
          <label className="text-xs font-medium text-muted-foreground">User</label>
          {selectedUser ? (
            <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/10 px-3 py-1.5 text-sm">
              <User className="h-4 w-4 text-primary" />
              <span className="font-medium text-primary">{selectedUser.displayName}</span>
              <button
                type="button"
                onClick={clearUserFilter}
                className="ml-1 cursor-pointer rounded-full p-0.5 transition-colors hover:bg-primary/20"
              >
                <X className="h-3 w-3 text-primary" />
              </button>
            </div>
          ) : (
            <div className="relative">
              <User className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                onFocus={() => userResults.length > 0 && setUserSearchOpen(true)}
                placeholder="Search by user..."
                className="w-64 pl-9"
              />
              {userSearchOpen && userResults.length > 0 && (
                <div className="absolute z-50 mt-1 max-h-72 w-80 overflow-y-auto rounded-lg border bg-card shadow-lg">
                  {userResults.map((u) => (
                    <button
                      key={u._id}
                      type="button"
                      className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-sm transition-colors first:rounded-t-lg last:rounded-b-lg hover:bg-accent"
                      onClick={() => {
                        setSelectedUser({ _id: u._id, displayName: u.displayName });
                        setUserQuery("");
                        setUserResults([]);
                        setUserSearchOpen(false);
                        setPage(1);
                      }}
                    >
                      {u.avatarUrl ? (
                        <img
                          src={u.avatarUrl}
                          alt=""
                          className="h-5 w-5 shrink-0 rounded-full object-cover"
                        />
                      ) : (
                        <User className="h-5 w-5 shrink-0 rounded-full bg-muted p-0.5 text-muted-foreground" />
                      )}
                      <div className="min-w-0 flex-1 text-left">
                        <div className="truncate font-medium">{u.displayName}</div>
                        <div className="truncate font-mono text-xs text-muted-foreground">
                          {u._id}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {[
            { label: "Deleted", icon: Trash2, checked: filterDeleted, onChange: setFilterDeleted },
            { label: "Reported", icon: Flag, checked: filterReported, onChange: setFilterReported },
          ].map(({ label, icon: Icon, checked, onChange }) => (
            <button
              key={label}
              type="button"
              onClick={() => {
                onChange(!checked);
                setPage(1);
              }}
              className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                checked
                  ? "border-primary/30 bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:bg-accent"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {commentsQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : commentsQuery.isError ? (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          Failed to load comments: {describeError(commentsQuery.error)}
        </div>
      ) : (
        <>
          <div className="grid gap-3">
            {data?.comments.map((comment) => (
              <CommentRow key={comment._id} comment={comment} />
            ))}
            {data?.comments.length === 0 && (
              <div className="py-16 text-center text-sm text-muted-foreground">No comments found</div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CommentRow({ comment }: { comment: AdminComment }) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [showReports, setShowReports] = useState(false);

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: COMMENTS_QUERY });
  }

  const deleteMutation = useMutation({
    mutationFn: () => api.comments.delete(comment._id),
    onSuccess: invalidate,
    onError: (e) => toast.error(describeError(e)),
  });

  const restoreMutation = useMutation({
    mutationFn: () => api.comments.restore(comment._id),
    onSuccess: invalidate,
    onError: (e) => toast.error(describeError(e)),
  });

  const staffMutation = useMutation({
    mutationFn: (next: boolean) =>
      api.comments.setUserStaff(comment.userId, next, comment.staticUserId),
    onSuccess: () => {
      toast.success("Staff status updated");
      invalidate();
    },
    onError: (e) => toast.error(describeError(e)),
  });

  const blockMutation = useMutation({
    mutationFn: (next: boolean) =>
      api.comments.setUserBlocked(comment.staticUserId ?? "", next),
    onSuccess: (_data, next) => {
      toast.success(next ? "User blocked" : "User unblocked");
      invalidate();
    },
    onError: (e) => toast.error(describeError(e)),
  });

  function handleDelete() {
    if (!confirm("Delete this comment? This will mark it as rejected and deleted.")) return;
    deleteMutation.mutate();
  }

  function handleRestore() {
    if (!confirm("Restore this comment? This will mark it as approved and visible.")) return;
    restoreMutation.mutate();
  }

  function handleToggleStaff() {
    const next = !comment.userIsStaff;
    if (
      !confirm(
        `${next ? "Grant" : "Remove"} staff status for "${comment.displayName}"? This updates all of their comments.`
      )
    )
      return;
    staffMutation.mutate(next);
  }

  function handleToggleBlock() {
    if (!comment.staticUserId) {
      toast.error("This comment has no static user id, so the user can't be blocked.");
      return;
    }
    const next = !comment.userIsBlocked;
    if (
      !confirm(
        `${next ? "Block" : "Unblock"} "${comment.displayName}"? ${
          next ? "They will no longer be able to comment." : "They will be able to comment again."
        }`
      )
    )
      return;
    blockMutation.mutate(next);
  }

  return (
    <div
      className={`rounded-xl bg-card p-4 shadow-sm ring-1 ${
        comment.isDeleted ? "bg-red-50/30 ring-red-200/60" : "ring-black/4"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          {/* Header */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold">{comment.displayName}</span>
            {comment.isStaff && (
              <span className="rounded bg-primary px-1.5 py-0.5 text-[10px] font-bold uppercase text-primary-foreground">
                Staff
              </span>
            )}
            <ModerationBadge status={comment.moderationStatus} />
            {comment.userIsBlocked && (
              <span className="flex items-center gap-1 rounded-md border border-red-300 bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-800">
                <Ban className="h-3 w-3" />
                Blocked
              </span>
            )}
            {comment.isDeleted && (
              <span className="flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
                <Trash2 className="h-3 w-3" />
                Deleted
              </span>
            )}
            {comment.reports.length > 0 && (
              <button
                type="button"
                onClick={() => setShowReports(!showReports)}
                className="flex cursor-pointer items-center gap-1 rounded-md border border-orange-200 bg-orange-50 px-2 py-0.5 text-[11px] font-medium text-orange-700 transition-colors hover:bg-orange-100"
              >
                <Flag className="h-3 w-3" />
                {comment.reports.length} report{comment.reports.length !== 1 ? "s" : ""}
              </button>
            )}
          </div>

          {/* Meta */}
          <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span>{formatDate(comment.createdAt)}</span>
            <span className="font-mono">{comment.postSlug}</span>
            {comment.userEmail && <span>{comment.userEmail}</span>}
            {comment.likesCount > 0 && (
              <span className="flex items-center gap-1">
                <Heart className="h-3 w-3" />
                {comment.likesCount}
              </span>
            )}
            {comment.parentId && (
              <span className="flex items-center gap-1">
                <Shield className="h-3 w-3" />
                Reply
              </span>
            )}
          </div>

          {/* Content */}
          <div className="mt-2 text-sm leading-relaxed">
            <div
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{
                __html:
                  comment.content.length > 200 && !expanded
                    ? comment.content.slice(0, 200) + "..."
                    : comment.content,
              }}
            />
            {comment.content.length > 200 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="mt-1 cursor-pointer text-xs font-medium text-primary hover:underline"
              >
                {expanded ? "Show less" : "Show more"}
              </button>
            )}
          </div>

          {comment.imageUrl && (
            <a
              href={comment.imageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block text-xs text-primary hover:underline"
            >
              View attached image
            </a>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2">
          <span
            className="cursor-pointer select-all font-mono text-[11px] text-muted-foreground"
            title="Comment ID"
          >
            {comment._id}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className={`h-7 w-7 ${
                comment.userIsStaff
                  ? "text-blue-600 hover:text-blue-700"
                  : "text-muted-foreground hover:text-blue-600"
              }`}
              onClick={handleToggleStaff}
              disabled={staffMutation.isPending}
              title={comment.userIsStaff ? "Remove staff status" : "Grant staff status"}
            >
              <Shield className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={`h-7 w-7 ${
                comment.userIsBlocked
                  ? "text-red-600 hover:text-red-700"
                  : "text-muted-foreground hover:text-red-600"
              }`}
              onClick={handleToggleBlock}
              disabled={blockMutation.isPending || !comment.staticUserId}
              title={
                !comment.staticUserId
                  ? "No static user id — cannot block"
                  : comment.userIsBlocked
                    ? "Unblock user"
                    : "Block user"
              }
            >
              <Ban className="h-3.5 w-3.5" />
            </Button>
            {comment.isDeleted ? (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-emerald-600 hover:text-emerald-700"
                onClick={handleRestore}
                disabled={restoreMutation.isPending}
                title="Restore comment"
              >
                <Undo2 className="h-3.5 w-3.5" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                title="Delete comment"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
          {comment.userIsStaff && (
            <span className="rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">
              Staff
            </span>
          )}
        </div>
      </div>

      {/* Reports detail */}
      {showReports && comment.reports.length > 0 && (
        <div className="mt-3 rounded-lg border border-orange-200 bg-orange-50 p-3">
          <h4 className="mb-2 text-xs font-semibold text-orange-800">Reports</h4>
          <div className="space-y-1">
            {comment.reports.map((r, i) => (
              <div key={i} className="text-xs text-orange-700">
                <span className="font-medium">{r.displayName || "User"}</span>
                {r.email && <span className="ml-1 text-orange-500">{r.email}</span>}
                <span className="ml-1 font-mono text-orange-500">({r.userId})</span>: {r.reason}{" "}
                <span className="text-orange-500">({formatDate(r.createdAt)})</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
